// The athlete's activities changed under every open view: one removed (the
// activity panel's Remove), merged, split back or restored. The backend sends
// no stream event for any of these, so the page that made the change was the
// only one that knew, and Stats, the coach panel and the v2 store beside it
// kept the old activity until a reload. activitiesAPI announces each change
// once it is committed; each view that holds activities listens and reads again.
//
// `removed`: the Strava ids no longer shown (as strings); `kind`: 'removed',
// 'merged', 'unmerged' or 'restored'.
export const ACTIVITIES_CHANGED = 'activities:changed';

const target = () => (typeof window !== 'undefined' && window?.dispatchEvent ? window : null);

export function announceActivitiesChanged({ kind, removed = [] } = {}) {
  const win = target();
  if (!win) return;
  const detail = { kind, removed: removed.filter((id) => id != null).map(String) };
  try {
    win.dispatchEvent(new CustomEvent(ACTIVITIES_CHANGED, { detail }));
  } catch { /* no CustomEvent here: nothing is listening either */ }
}

/** Calls `fn(detail)` on every change; returns the unsubscribe. */
export function onActivitiesChanged(fn) {
  const win = target();
  if (!win) return () => {};
  const handler = (e) => fn(e?.detail || { removed: [] });
  win.addEventListener(ACTIVITIES_CHANGED, handler);
  return () => win.removeEventListener(ACTIVITIES_CHANGED, handler);
}

/** `activities` (the v2 store's, by id) less the ones a change removed (matched on their Strava id). */
export function withoutRemoved(activities, removed = []) {
  if (!activities || !removed.length) return activities;
  const gone = new Set(removed.map(String));
  const kept = Object.fromEntries(Object.entries(activities)
    .filter(([, a]) => !gone.has(String(a?.strava_activity_id))));
  return Object.keys(kept).length === Object.keys(activities).length ? activities : kept;
}

/** Whether a thread `context_ref` ('activity:<strava id>') names one of the activities removed. */
export function aboutRemovedActivity(ref, removed = []) {
  const [kind, id] = String(ref ?? '').split(':');
  return kind === 'activity' && Boolean(id) && removed.map(String).includes(id);
}

/**
 * The activities known to be gone (Strava ids as strings) after a change:
 * a removal or merge adds the ones it took away; a restore or an unmerge may
 * bring any back (a restore names none), so nothing is assumed gone any more.
 * An open that answers 404 adds that one again. Same Set when nothing changed.
 */
export function knownRemoved(known, { kind, removed = [] } = {}) {
  if (kind === 'restored' || kind === 'unmerged') return known.size ? new Set() : known;
  const ids = removed.filter((id) => id != null).map(String).filter((id) => !known.has(id));
  return ids.length ? new Set([...known, ...ids]) : known;
}
